import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, FileText, HeartPulse, Mail, Lock, CheckCircle2 } from 'lucide-react';

const LEGAL_CONTENT = {
  en: {
    lastUpdated: 'Last updated: November 2026',
    nav: {
      medical: 'Medical Disclaimer',
      privacy: 'Privacy & Cookie Policy',
      terms: 'Terms of Sale & Refunds',
    },
    medical: {
      badge: 'Clinical & Nutritional Compliance',
      title: 'Medical & Nutritional Guidance Disclaimer',
      subtitle:
        'Important information regarding the scope of clinical nutrition consultations, meal plans, and educational content provided by Paola Michelle (TODOenBALANCE).',
      sections: [
        {
          heading: '1. Scope of Nutritional Practice',
          body: 'TODOenBALANCE is operated by Paola Michelle, a qualified Clinical Nutritionist (BSc in Nutrition, UANL; MSc in Clinical Nutrition & Endocrinology, UCAM). All 1-on-1 consultations, personalized meal plans, downloadable diet guides (eBooks), and blog articles are intended strictly for nutritional guidance, dietary education, and healthy lifestyle support.',
        },
        {
          heading: '2. Not a Substitute for Emergency or Primary Medical Care',
          body: 'Nutritional counseling is not a substitute for independent medical diagnosis, emergency medical intervention, or psychiatric treatment. Do not disregard, delay, or discontinue professional medical advice or prescribed medication from your primary care physician or specialist because of information read on this website or discussed in a nutrition session.',
        },
        {
          heading: '3. Patient Responsibility & Disclosure of Allergies / Pathologies',
          body: 'Before starting any personalized meal plan or downloadable diet guide, clients must disclose all known food allergies, intolerances (such as celiac disease, lactose intolerance, or nut allergies), diagnosed medical conditions (including diabetes, renal disease, pregnancy, or eating disorders), and current prescription medications. You are solely responsible for verifying food labels and ingredients before consumption.',
        },
        {
          heading: '4. Individual Results & Clinical Safety',
          body: 'Metabolic response, weight management, and body composition changes vary from person to person depending on genetics, adherence, sleep, physical activity, and underlying health status. TODOenBALANCE does not guarantee specific weight-loss numbers within fixed timeframes and promotes sustainable, evidence-based habits.',
        },
        {
          heading: '5. Contact for Clinical Questions',
          body: 'If you experience any adverse symptoms or have questions regarding your nutritional plan, please pause the plan and contact Paola Michelle directly at paola@todoenbalance.com or consult your physician immediately.',
        },
      ],
    },
    privacy: {
      badge: 'GDPR & Data Protection (EU 2016/679)',
      title: 'Privacy Policy & Cookie Notice',
      subtitle:
        'How TODOenBALANCE collects, protects, and processes your personal and health-related data in accordance with the European Union General Data Protection Regulation (GDPR).',
      sections: [
        {
          heading: '1. Data Controller Identity',
          body: 'Data Controller: Paola Michelle — TODOenBALANCE Clinical Nutrition Practice. Contact emails: paola@todoenbalance.com and info@todoenbalance.com. Operating online across the European Union (Italy / Spain) and internationally.',
        },
        {
          heading: '2. Categories of Personal & Clinical Data Collected',
          body: 'We collect only the minimum data necessary to deliver our services: (a) Identification & Contact Data: full name, email address, phone/WhatsApp number, and timezone; (b) Clinical & Nutritional Data (with explicit consent under GDPR Art. 9(2)(a)): anthropometric measurements, dietary habits, allergies, health goals, and voluntary lab markers shared for 1-on-1 consultations; (c) Transaction Data: purchase history, consultation timestamps, and payment status processed via Stripe (we never store raw credit card numbers).',
        },
        {
          heading: '3. Legal Basis & Purpose of Processing',
          body: 'Your data is processed to: (1) schedule and deliver 1-on-1 video consultations and personalized meal plans (Contractual necessity, GDPR Art. 6(1)(b)); (2) provide clinical nutritional care based on your explicit consent (GDPR Art. 9(2)(a)); (3) deliver purchased PDF diet guides and payment receipts via email; and (4) comply with applicable tax and accounting obligations.',
        },
        {
          heading: '4. Trusted Third-Party Processors & Security',
          body: 'We never sell or rent your personal data. Data is shared strictly with GDPR-compliant infrastructure providers: Stripe Payments Europe Ltd. (PCI-DSS Level 1 payment processing), Resend (transactional booking and download emails), Neon / Render (encrypted database hosting), and Vercel (secure web hosting). All data is transmitted over TLS/HTTPS encryption.',
        },
        {
          heading: '5. Data Retention & Your GDPR Rights',
          body: 'Under GDPR Articles 15–22, you have the right to access, rectify, port, restrict, or request erasure ("Right to be Forgotten") of your personal and clinical file at any time, as well as to withdraw consent. To exercise your rights, email paola@todoenbalance.com and we will respond within 30 days.',
        },
        {
          heading: '6. Cookie Notice',
          body: 'Our website uses strictly necessary technical storage (such as your selected language preference EN/ES/IT, timezone selection, and secure Stripe checkout session tokens) and anonymous, cookie-free performance metrics via Vercel Analytics. We do not use invasive third-party advertising trackers.',
        },
      ],
    },
    terms: {
      badge: 'Stripe Merchant & Consumer Terms',
      title: 'Terms of Sale, Cancellation & Refund Policy',
      subtitle:
        'Clear terms governing 1-on-1 online nutrition consultations, digital PDF meal plan downloads, secure Stripe payments, and EU consumer withdrawal rights.',
      sections: [
        {
          heading: '1. Services & Digital Products Offered',
          body: 'TODOenBALANCE offers two categories of products: (a) Live 1-on-1 Online Nutrition Consultations (30-minute and 60-minute video sessions with personalized follow-up); and (b) Downloadable Digital Diet Guides & Meal Plan eBooks (delivered immediately in PDF format upon payment). All prices are displayed transparently in Euros (€) inclusive of applicable taxes.',
        },
        {
          heading: '2. Secure Payment Processing via Stripe',
          body: 'Payments are processed securely in real time via Stripe Checkout (supporting major credit/debit cards, Apple Pay, Google Pay, and local payment methods). Your booking or digital order is confirmed immediately once Stripe verifies payment authorization.',
        },
        {
          heading: '3. Digital Diet Guides (PDF eBooks) — Immediate Delivery & Refund Policy',
          body: 'When purchasing a downloadable PDF diet plan, you receive immediate access to download the digital file plus an email backup link. In accordance with Article 16(m) of EU Directive 2011/83/EU on Consumer Rights, by completing a digital download purchase you expressly consent to immediate performance and acknowledge that you waive your 14-day statutory right of withdrawal once download access is granted. However, if you experience any technical issue downloading your PDF or receive a corrupted file, contact info@todoenbalance.com within 14 days and we will promptly send a working copy or issue a full refund.',
        },
        {
          heading: '4. 1-on-1 Nutrition Consultations — Rescheduling & Refund Policy',
          body: 'We understand schedules can change: (a) Free Rescheduling or Full Refund: You may reschedule or cancel your consultation for a 100% refund up to 24 hours before your scheduled appointment time by emailing paola@todoenbalance.com; (b) Late Cancellations (< 24 hours) or No-Shows: Cancellations made less than 24 hours before the session or missed appointments without prior notice are non-refundable, as the clinical slot was reserved exclusively for you; (c) Nutritionist Cancellation: In the rare event Paola Michelle needs to reschedule, you will be offered the earliest priority slot or an immediate 100% refund.',
        },
        {
          heading: '5. Intellectual Property & Personal Use License',
          body: 'All downloadable PDF meal plans, recipe books, and clinical materials are copyrighted by TODOenBALANCE (Paola Michelle). Purchase grants a single-user, non-transferable license for personal use only. Redistribution, resale, or public uploading of PDF guides is strictly prohibited.',
        },
        {
          heading: '6. Customer Support & Dispute Resolution',
          body: 'Customer satisfaction is our top priority. For any billing inquiry, download assistance, or refund request, contact info@todoenbalance.com or paola@todoenbalance.com. We resolve all support inquiries within 24–48 business hours.',
        },
      ],
    },
  },
  es: {
    lastUpdated: 'Última actualización: Noviembre 2026',
    nav: {
      medical: 'Aviso Médico y Nutricional',
      privacy: 'Privacidad y Cookies (RGPD)',
      terms: 'Condiciones de Venta y Reembolsos',
    },
    medical: {
      badge: 'Cumplimiento Clínico y Sanitario',
      title: 'Descargo de Responsabilidad Médica y Nutricional',
      subtitle:
        'Información importante sobre el alcance de las consultas de nutrición clínica, planes de alimentación y contenidos educativos ofrecidos por Paola Michelle (TODOenBALANCE).',
      sections: [
        {
          heading: '1. Alcance de la Práctica Nutricional',
          body: 'TODOenBALANCE está dirigido por Paola Michelle, Nutricionista Clínica titulada (Licenciada en Nutrición por la UANL; Máster en Nutrición Clínica y Endocrinología avalado por la UCAM). Todas las consultas 1 a 1, planes nutricionales personalizados, guías descargables en PDF y artículos del blog tienen una finalidad de asesoramiento nutricional, educación alimentaria y mejora de hábitos de salud.',
        },
        {
          heading: '2. No Sustituye la Atención Médica de Urgencia ni el Diagnóstico Médico',
          body: 'El asesoramiento nutricional no sustituye el diagnóstico médico facultativo, la atención médica de urgencia ni los tratamientos farmacológicos o psiquiátricos. Nunca ignore, retrase ni suspenda un tratamiento médico recetado por su médico de cabecera o especialista debido a la información leída en este sitio web.',
        },
        {
          heading: '3. Responsabilidad del Paciente y Declaración de Alergias / Patologías',
          body: 'Antes de iniciar cualquier plan nutricional personalizado o guía descargable, el paciente debe informar sobre cualquier alergia o intolerancia alimentaria (como celiaquía, intolerancia a la lactosa o alergia a frutos secos), patologías diagnosticadas (diabetes, enfermedad renal, embarazo, trastornos de la conducta alimentaria) y medicación actual. El usuario es responsable de verificar el etiquetado de los ingredientes antes de su consumo.',
        },
        {
          heading: '4. Resultados Individuales y Seguridad Clínica',
          body: 'La respuesta metabólica y los cambios en la composición corporal varían según cada persona, su genética, adherencia, descanso y actividad física. TODOenBALANCE promueve hábitos sostenibles basados en evidencia científica y no garantiza pérdidas de peso exactas en plazos fijos.',
        },
        {
          heading: '5. Contacto para Consultas Clínicas',
          body: 'Si experimenta cualquier síntoma adverso o tiene dudas sobre su pauta nutricional, suspenda el plan y escriba directamente a Paola Michelle en paola@todoenbalance.com o consulte a su médico.',
        },
      ],
    },
    privacy: {
      badge: 'RGPD y Protección de Datos (UE 2016/679)',
      title: 'Política de Privacidad y Aviso de Cookies',
      subtitle:
        'Cómo TODOenBALANCE recopila, protege y trata sus datos personales y de salud conforme al Reglamento General de Protección de Datos (RGPD) de la Unión Europea.',
      sections: [
        {
          heading: '1. Responsable del Tratamiento',
          body: 'Responsable: Paola Michelle — Consulta de Nutrición Clínica TODOenBALANCE. Correos de contacto: paola@todoenbalance.com e info@todoenbalance.com. Servicio online en la Unión Europea (España / Italia) e internacional.',
        },
        {
          heading: '2. Categorías de Datos Personales y Clínicos Recopilados',
          body: 'Recopilamos únicamente los datos mínimos necesarios para prestar nuestros servicios: (a) Datos identificativos y de contacto: nombre completo, correo electrónico, teléfono/WhatsApp y zona horaria; (b) Datos clínicos y nutricionales (con su consentimiento explícito según el Art. 9.2.a del RGPD): antropometría, historia dietética, alergias, objetivos de salud y analíticas aportadas para su consulta 1 a 1; (c) Datos de transacción: historial de reservas y estado del pago procesado de forma segura mediante Stripe (nunca almacenamos datos de tarjetas bancarias).',
        },
        {
          heading: '3. Finalidad y Base Jurídica del Tratamiento',
          body: 'Sus datos se tratan para: (1) gestionar sus videoconsultas 1 a 1 y elaborar su plan nutricional personalizado (Ejecución de contrato, Art. 6.1.b RGPD); (2) prestar atención nutricional clínica bajo su consentimiento explícito (Art. 9.2.a RGPD); (3) enviar por email las guías PDF adquiridas y confirmaciones de cita; y (4) cumplir con las obligaciones fiscales y legales aplicables.',
        },
        {
          heading: '4. Encargados del Tratamiento y Seguridad',
          body: 'Nunca vendemos ni cedemos sus datos a terceros con fines comerciales. Utilizamos proveedores tecnológicos que cumplen estrictamente con el RGPD: Stripe Payments Europe Ltd. (pagos certificados PCI-DSS Nivel 1), Resend (envío de correos transaccionales), Neon / Render (base de datos cifrada) y Vercel (alojamiento web seguro bajo cifrado TLS/HTTPS).',
        },
        {
          heading: '5. Conservación de Datos y Derechos ARCO / RGPD',
          body: 'Conforme a los artículos 15 a 22 del RGPD, puede ejercer en cualquier momento sus derechos de acceso, rectificación, supresión ("derecho al olvido"), limitación, portabilidad y revocación del consentimiento escribiendo a paola@todoenbalance.com. Atenderemos su solicitud en un plazo máximo de 30 días.',
        },
        {
          heading: '6. Política de Cookies',
          body: 'Este sitio web utiliza almacenamiento técnico estrictamente necesario (preferencia de idioma ES/EN/IT, zona horaria seleccionada y sesión de pago seguro de Stripe) y métricas anónimas sin cookies de rastreo publicitario a través de Vercel Analytics.',
        },
      ],
    },
    terms: {
      badge: 'Condiciones de Compra y Reembolsos',
      title: 'Condiciones de Venta, Cancelación y Política de Reembolso',
      subtitle:
        'Términos transparentes aplicables a las videoconsultas nutricionales 1 a 1, descargas digitales de planes en PDF, pagos seguros con Stripe y derechos del consumidor en la UE.',
      sections: [
        {
          heading: '1. Servicios y Productos Digitales Ofrecidos',
          body: 'TODOenBALANCE ofrece dos modalidades: (a) Consultas de Nutrición Online 1 a 1 (sesiones por videollamada de 30 y 60 minutos con plan personalizado); y (b) Guías Nutricionales y Planes de Alimentación Descargables en PDF (entrega digital inmediata tras el pago). Todos los precios se muestran en Euros (€) con impuestos incluidos.',
        },
        {
          heading: '2. Procesamiento de Pagos Seguros con Stripe',
          body: 'Todos los pagos se procesan en tiempo real a través de la pasarela segura Stripe Checkout (tarjetas de crédito/débito, Apple Pay, Google Pay). Su reserva o descarga se activa inmediatamente tras la confirmación del pago.',
        },
        {
          heading: '3. Planes Descargables en PDF (eBooks) — Entrega Inmediata y Reembolsos',
          body: 'Al adquirir una guía o plan descargable en PDF, usted obtiene acceso inmediato al archivo digital además de una copia en su correo electrónico. De conformidad con el Artículo 16(m) de la Directiva 2011/83/UE sobre los Derechos de los Consumidores, al confirmar la compra de contenido digital de descarga inmediata usted consiente expresamente el inicio de la descarga y reconoce que pierde el derecho de desistimiento de 14 días una vez habilitado el acceso al PDF. No obstante, si experimenta cualquier problema técnico con el enlace de descarga, escríbanos a info@todoenbalance.com dentro de los 14 días posteriores y le reenviaremos el archivo o realizaremos el reembolso íntegro.',
        },
        {
          heading: '4. Consultas de Nutrición 1 a 1 — Cambios de Cita y Reembolsos',
          body: 'Sabemos que pueden surgir imprevistos: (a) Reprogramación Gratuita o Reembolso del 100%: Puede cambiar la fecha de su cita o cancelar con reembolso íntegro avisando con al menos 24 horas de antelación a paola@todoenbalance.com; (b) Cancelaciones con menos de 24 horas o Inasistencia (No-Show): Las cancelaciones realizadas con menos de 24 horas de antelación o la no asistencia a la videollamada no son reembolsables, dado que la hora clínica fue bloqueada exclusivamente para usted; (c) Cancelación por parte de la Nutricionista: Si por causa de fuerza mayor Paola Michelle debiera reprogramar la sesión, se le ofrecerá prioridad de horario o el reembolso inmediato del 100%.',
        },
        {
          heading: '5. Propiedad Intelectual y Licencia de Uso Personal',
          body: 'Todos los planes nutricionales en PDF, recetarios y materiales clínicos son propiedad intelectual de TODOenBALANCE (Paola Michelle). La compra otorga una licencia de uso estrictamente personal e intransferible. Queda prohibida su reventa, distribución o publicación sin autorización.',
        },
        {
          heading: '6. Atención al Cliente y Soporte de Pedidos',
          body: 'Para cualquier consulta sobre facturación, ayuda con su descarga PDF o solicitud de cambio/reembolso, contáctenos en info@todoenbalance.com o paola@todoenbalance.com. Respondemos en menos de 24–48 horas hábiles.',
        },
      ],
    },
  },
  it: {
    lastUpdated: 'Ultimo aggiornamento: Novembre 2026',
    nav: {
      medical: 'Disclaimer Medico e Nutrizionale',
      privacy: 'Privacy e Cookie Policy (GDPR)',
      terms: 'Termini di Vendita e Rimborsi',
    },
    medical: {
      badge: 'Conformità Clinica e Nutrizionale',
      title: 'Disclaimer Medico e di Consulenza Nutrizionale',
      subtitle:
        'Informazioni importanti sull’ambito delle consulenze nutrizionali online, dei piani alimentari e dei contenuti educativi forniti da Paola Michelle (TODOenBALANCE).',
      sections: [
        {
          heading: '1. Ambito della Pratica Nutrizionale',
          body: 'TODOenBALANCE è gestito da Paola Michelle, Nutrizionista Clinica qualificata (Laurea in Nutrizione presso UANL; Master in Nutrizione Clinica ed Endocrinologia presso UCAM). Tutte le consulenze 1-a-1, i piani alimentari personalizzati, le guide scaricabili in PDF e gli articoli del blog hanno finalità di educazione alimentare, supporto nutrizionale e promozione di uno stile di vita sano.',
        },
        {
          heading: '2. Non Sostituisce le Cure Mediche di Emergenza o la Diagnosi Medica',
          body: 'La consulenza nutrizionale non sostituisce la diagnosi medica, gli interventi di emergenza né le terapie farmacologiche o psichiatriche prescritte dal medico curante o dallo specialista. Non interrompere o modificare mai una terapia medica senza aver prima consultato il proprio medico.',
        },
        {
          heading: '3. Responsabilità del Paziente e Comunicazione di Allergie / Patologie',
          body: 'Prima di iniziare qualsiasi piano alimentare personalizzato o guida scaricabile, il cliente è tenuto a segnalare tutte le allergie o intolleranze alimentari note (es. celiachia, intolleranza al lattosio, allergia alla frutta a guscio), patologie diagnosticate (diabete, patologie renali, gravidanza, disturbi alimentari) e terapie farmacologiche in corso. Il cliente è responsabile della verifica delle etichette alimentari prima del consumo.',
        },
        {
          heading: '4. Risultati Individuali e Sicurezza Clinica',
          body: 'La risposta metabolica e i cambiamenti nella composizione corporea variano da persona a persona in base a genetica, costanza, riposo, attività fisica e stato di salute. TODOenBALANCE promuove abitudini sostenibili basate sull’evidenza scientifica e non garantisce perdite di peso prefissate in tempi standard.',
        },
        {
          heading: '5. Contatti per Domande Cliniche',
          body: 'In caso di dubbi o sintomi inattesi durante il percorso nutrizionale, sospendere il piano e contattare direttamente Paola Michelle all’indirizzo paola@todoenbalance.com o rivolgersi al proprio medico curante.',
        },
      ],
    },
    privacy: {
      badge: 'GDPR e Protezione dei Dati (UE 2016/679)',
      title: 'Informativa sulla Privacy e Cookie Notice',
      subtitle:
        'Come TODOenBALANCE raccoglie, protegge e tratta i tuoi dati personali e sanitari in conformità al Regolamento Generale sulla Protezione dei Dati dell’Unione Europea (GDPR).',
      sections: [
        {
          heading: '1. Titolare del Trattamento dei Dati',
          body: 'Titolare del Trattamento: Paola Michelle — Studio di Nutrizione Clinica TODOenBALANCE. Email di contatto: paola@todoenbalance.com e info@todoenbalance.com. Attività svolta online nell’Unione Europea (Italia / Spagna) e a livello internazionale.',
        },
        {
          heading: '2. Categorie di Dati Personali e Clinici Raccolti',
          body: 'Raccogliamo esclusivamente i dati minimi necessari per erogare i nostri servizi: (a) Dati identificativi e di contatto: nome completo, indirizzo email, numero di telefono/WhatsApp e fuso orario; (b) Dati clinici e nutrizionali (previo consenso esplicito ai sensi dell’Art. 9(2)(a) del GDPR): misurazioni antropometriche, abitudini alimentari, allergie, obiettivi di salute ed esami ematochimici condivisi per le consulenze 1-a-1; (c) Dati di transazione: storico degli ordini e stato del pagamento gestito in sicurezza tramite Stripe (non memorizziamo mai i numeri delle carte di credito).',
        },
        {
          heading: '3. Base Giuridica e Finalità del Trattamento',
          body: 'I dati sono trattati per: (1) pianificare ed erogare le video-consulenze 1-a-1 e i piani alimentari personalizzati (Esecuzione contrattuale, Art. 6(1)(b) GDPR); (2) fornire assistenza nutrizionale clinica sulla base del consenso esplicito (Art. 9(2)(a) GDPR); (3) inviare via email le guide PDF acquistate e le ricevute d’ordine; e (4) adempiere agli obblighi fiscali e contabili vigenti.',
        },
        {
          heading: '4. Responsabili Esterni del Trattamento e Sicurezza',
          body: 'Non vendiamo né cediamo mai i tuoi dati personali a terzi. I dati sono trattati esclusivamente tramite fornitori conformi al GDPR: Stripe Payments Europe Ltd. (pagamenti certificati PCI-DSS Livello 1), Resend (email transazionali), Neon / Render (database crittografato) e Vercel (hosting protetto da crittografia TLS/HTTPS).',
        },
        {
          heading: '5. Conservazione dei Dati e Diritti dell’Interessato (GDPR)',
          body: 'Ai sensi degli articoli 15–22 del GDPR, hai il diritto di chiedere in qualsiasi momento l’accesso, la rettifica, la portabilità, la limitazione o la cancellazione ("Diritto all’Oblio") dei tuoi dati personali e clinici, nonché di revocare il consenso scrivendo a paola@todoenbalance.com. Risponderemo entro 30 giorni.',
        },
        {
          heading: '6. Informativa sui Cookie',
          body: 'Il nostro sito utilizza esclusivamente archiviazione tecnica strettamente necessaria (preferenza lingua IT/ES/EN, fuso orario selezionato e sessione di pagamento Stripe) e statistiche anonime prive di cookie di profilazione pubblicitaria tramite Vercel Analytics.',
        },
      ],
    },
    terms: {
      badge: 'Condizioni di Vendita e Rimborsi',
      title: 'Termini di Vendita, Cancellazione e Politica di Rimborso',
      subtitle:
        'Condizioni chiare e trasparenti per le video-consulenze nutrizionali 1-a-1, il download immediato dei piani alimentari in PDF, i pagamenti sicuri tramite Stripe e i diritti dei consumatori UE.',
      sections: [
        {
          heading: '1. Servizi e Prodotti Digitali Offerti',
          body: 'TODOenBALANCE offre due categorie di servizi: (a) Consulenze Nutrizionali Online 1-a-1 (sessioni video da 30 e 60 minuti con piano personalizzato); e (b) Guide Alimentari e Piani Dietetici Digitali in PDF (consegna immediata tramite download dopo il pagamento). Tutti i prezzi sono indicati chiaramente in Euro (€) comprensivi delle imposte applicabili.',
        },
        {
          heading: '2. Pagamenti Sicuri tramite Stripe',
          body: 'I pagamenti vengono elaborati in tempo reale tramite Stripe Checkout (carte di credito/debito, Apple Pay, Google Pay). La prenotazione o il link di download PDF vengono confermati immediatamente dopo l’autorizzazione del pagamento.',
        },
        {
          heading: '3. Guide Digitali in PDF (eBook) — Consegna Immediata e Politica di Rimborso',
          body: 'Acquistando un piano alimentare scaricabile in PDF ricevi accesso immediato al download del file digitale oltre a un link di backup via email. Ai sensi dell’Articolo 16(m) della Direttiva UE 2011/83/UE sui Diritti dei Consumatori, completando l’acquisto di un contenuto digitale a consegna immediata acconsenti espressamente all’esecuzione immediata e prendi atto della rinuncia al diritto di recesso di 14 giorni una volta attivato il download. Tuttavia, in caso di problemi tecnici con il download o file danneggiato, contatta info@todoenbalance.com entro 14 giorni e provvederemo subito a inviare una copia funzionante o a emettere un rimborso completo.',
        },
        {
          heading: '4. Consulenze Nutrizionali 1-a-1 — Spostamento Appuntamento e Rimborsi',
          body: 'Comprendiamo gli imprevisti: (a) Spostamento Gratuito o Rimborso del 100%: Puoi riprogrammare o annullare la consulenza con rimborso completo fino a 24 ore prima dell’orario fissato scrivendo a paola@todoenbalance.com; (b) Cancellazioni Tardive (< 24 ore) o Mancata Presentazione (No-Show): Le cancellazioni effettuate a meno di 24 ore dalla sessione o la mancata partecipazione non sono rimborsabili, poiché lo slot clinico è stato riservato esclusivamente a te; (c) Spostamento da parte della Nutrizionista: Nel raro caso in cui Paola Michelle dovesse riprogrammare la seduta, ti verrà offerto il primo orario prioritario disponibile o il rimborso immediato del 100%.',
        },
        {
          heading: '5. Proprietà Intellettuale e Licenza d’Uso Personale',
          body: 'Tutti i piani alimentari in PDF, i ricettari e i materiali clinici sono protetti da copyright di TODOenBALANCE (Paola Michelle). L’acquisto concede una licenza individuale non trasferibile per solo uso personale. È vietata la rivendita, la distribuzione o la pubblicazione dei file PDF.',
        },
        {
          heading: '6. Assistenza Clienti e Risoluzione Controversie',
          body: 'La soddisfazione dei nostri pazienti e clienti è prioritaria. Per qualsiasi richiesta relativa a fatturazione, download PDF o rimborsi, scrivi a info@todoenbalance.com o paola@todoenbalance.com. Rispondiamo entro 24–48 ore lavorative.',
        },
      ],
    },
  },
};

const LegalLayout = ({ docKey }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const copy = LEGAL_CONTENT[lang] || LEGAL_CONTENT.en;
  const doc = copy[docKey] || copy.medical;

  const tabs = [
    { path: '/medical-disclaimer', label: copy.nav.medical, icon: HeartPulse },
    { path: '/privacy', label: copy.nav.privacy, icon: ShieldCheck },
    { path: '/terms', label: copy.nav.terms, icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/40 via-white to-sky-50/40 py-14 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Legal Navigation Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = location.pathname === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all ${
                  active
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </Link>
            );
          })}
        </div>

        {/* Document Card */}
        <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-8 sm:p-12">
          <div className="border-b border-gray-100 pb-8 mb-8">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-3">
              <Lock className="w-3.5 h-3.5" />
              {doc.badge}
            </span>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {doc.title}
            </h1>
            <p className="mt-3 text-base text-gray-600 leading-relaxed">{doc.subtitle}</p>
            <p className="mt-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {copy.lastUpdated} · TODOenBALANCE (Paola Michelle)
            </p>
          </div>

          <div className="space-y-8">
            {doc.sections.map((section, idx) => (
              <section key={idx} className="space-y-2">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  {section.heading}
                </h2>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed pl-7">
                  {section.body}
                </p>
              </section>
            ))}
          </div>

          {/* Direct Compliance Contact Box */}
          <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 rounded-2xl p-6">
            <div>
              <p className="text-sm font-bold text-gray-900">
                TODOenBALANCE · Paola Michelle (MSc Clinical Nutrition)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                paola@todoenbalance.com · info@todoenbalance.com
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white text-xs font-bold px-5 py-3 rounded-full transition-colors"
            >
              <Mail className="w-4 h-4" />
              {lang === 'es'
                ? 'Contactar Soporte'
                : lang === 'it'
                ? 'Contatta il Supporto'
                : 'Contact Support'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MedicalDisclaimerPage = () => <LegalLayout docKey="medical" />;
export const PrivacyPolicyPage = () => <LegalLayout docKey="privacy" />;
export const TermsOfSalePage = () => <LegalLayout docKey="terms" />;
export default LegalLayout;
